import type { BasePersonInput, ContributionInputType as BaseContributionInputType } from './base';
import type { ContractorBreakdown } from './contractor';
import type { ManualEntry } from './tracking';

// Options names matching backend enum
export enum StrategyName {
  // PAYE strategies
  MaximiseCashInHand = "MaximiseCashInHand",
  MinimiseTax = "MinimiseTax",
  SecureChildBenefit = "SecureChildBenefit",
  Escape100kTrap = "Escape100kTrap",
  SwitchToSalarySacrifice = "SwitchToSalarySacrifice",

  // Contractor strategies
  ContractorBaseline = "ContractorBaseline",
  ContractorMinimiseTax = "ContractorMinimiseTax",
  ContractorWithFlatRateVAT = "ContractorWithFlatRateVAT",
  ContractorEscape100kTrap = "ContractorEscape100kTrap",
  ContractorSecureChildBenefit = "ContractorSecureChildBenefit",
  ContractorEscape90kVATRegistration = "ContractorEscape90kVATRegistration",
  ContractorMarginalReliefOptimisation = "ContractorMarginalReliefOptimisation"
}

/**
 * Discriminated free-childcare impact for a household.
 * Mirrors the Rust `FreeChildcareImpact` enum (internally tagged on `type`).
 */
export type FreeChildcareImpact =
  | { type: 'not_eligible' }
  | { type: 'eligible'; value: number }
  | { type: 'reduced'; value: number; lost: number }
  | { type: 'lost'; lost: number };

/**
 * Discriminated tax-free-childcare impact for a household.
 * Mirrors the Rust `TaxFreeChildcareImpact` enum.
 */
export type TaxFreeChildcareImpact =
  | { type: 'not_eligible' }
  | { type: 'eligible'; value: number }
  | { type: 'lost'; lost: number };

/**
 * Structured suggestion tip emitted by the calculator.
 * Mirrors the Rust `SuggestionTip` enum (internally tagged).
 */
export type SuggestionTip =
  | { type: 'tapered_aa_exhausted' }
  | { type: 'tapered_aa_active' }
  | { type: 'child_benefit_taper_reducible'; contribution: number; charge: number }
  | { type: 'restore_free_childcare'; contribution: number }
  | { type: 'restore_personal_allowance'; contribution: number }
  | { type: 'annual_allowance_charge'; charge: number };

// A single "what-if" strategy outcome for a household
export interface StrategyResult {
  name: StrategyName | null;
  recommended_pension: number;
  recommended_charity: number;
  cash_in_hand: number;
  total_tax: number;
  national_insurance: number;
  taxable_benefits: number;
  suggested_pension: number;
  suggested_charity: number;
  projected_tax_savings_vs_max_cash: number;
  free_childcare: FreeChildcareImpact;
  tax_free_childcare: TaxFreeChildcareImpact;
  child_benefit_repayment: number;
  description: string;
  contractor_breakdown?: ContractorBreakdown;
}

export interface ChartData {
  income: number;
  rate: number;
}

export interface BreakdownData {
  name: string;
  value: number;
}

// Re-export base types for convenience
export type { BasePersonInput, PensionType, CharityType, ContributionInputType } from './base';

/**
 * Person input for PAYE employees.
 * Extends BasePersonInput with PAYE-specific fields.
 */
export interface PersonInput extends BasePersonInput {
  // PAYE-specific fields
  salary_sacrifice_contributions: number;
  taxable_benefits?: number;
  bonus?: number;

  // Employer pension contributions (only for PAYE employees)
  employer_pension_contributions_percentage?: number;
  employer_pension_contributions_amount?: number;
  employer_pension_contributions_type?: BaseContributionInputType;
}

export interface ChildInput {
  age: number;
}

export interface HouseholdInput {
  primary: PersonInput;
  partner?: PersonInput;
  children: ChildInput[];
  claims_child_benefit?: boolean;
  preferred_strategy?: StrategyName | null;
  payslip_reminder_day?: number | null;
  tracker_manual_entries?: ManualEntry[] | null;
  /** Tax year for calculations (e.g., "2024-25"). Optional - defaults to current year. */
  tax_year?: string;
}

export interface HouseholdReport {
  /** Tax year this report is calculated for (e.g., "2025-26") */
  tax_year: string;
  adjusted_annual_income: number;
  adjusted_net_income?: number;
  cash_in_hand: number;
  rebate_due: number;
  actual_tax: number;
  tax_paid: number;
  national_insurance: number;
  taxable_benefits: number;
  has_children: boolean;
  free_childcare: FreeChildcareImpact;
  tax_free_childcare: TaxFreeChildcareImpact;
  child_benefit_repayment: number;
  suggestion_to_reduce_tax: SuggestionTip[];
  tax_savings_due_vs_max_cash: number;
  pension_contributions_grossed_up: number;
  can_contribute_more_to_sipp: boolean;
  remaining_sipp_allowance: number;
  annual_allowance_charge: number;
  charity_gross_donation: number;
  charity_reclaim: number;
  charity_personal_relief: number;

  // Newly added strategy outcomes
  strategies: StrategyResult[];

  // Tax saved compared to 0 pension and 0 charity contributions
  tax_saved: number;
  // Total value of benefits secured compared to 0 pension and 0 charity contributions
  benefits_secured: number;
}

// Re-export contractor types
export * from './contractor';
